import type { AuthenticatedUser, UserRegistrationType } from "@brainics/api-contract";
import { useState, type ReactNode } from "react";

import { useAuth } from "../auth/AuthProvider.js";
import { authErrorMessage } from "../lib/api.js";
import { navigate, type WorkspaceRouteName, workspaceRoutePaths } from "../lib/routing.js";
import {
  CompanyFilesPage,
  CompanyMembersPage,
  CompanyModerationPage,
  CompanyProfilePage,
} from "./company/CompanyCabinet.js";
import {
  SpecialistAvailabilityPage,
  SpecialistEducationPage,
  SpecialistExperiencePage,
  SpecialistPortfolioPage,
  SpecialistProfilePage,
  SpecialistRatesPage,
  SpecialistSkillsPage,
} from "./specialist/SpecialistCabinet.js";

const registrationTypeLabels: Record<UserRegistrationType, string> = {
  client_owner: "Заказчик",
  vendor_owner: "Подрядчик",
  independent_specialist: "Независимый специалист",
  invited_member: "Участник компании",
  platform_staff: "Команда Brainics",
};

const roleHints: Record<UserRegistrationType, string> = {
  client_owner: "Кабинет заказчика: компания, команда, вакансии и подбор.",
  vendor_owner: "Кабинет подрядчика: компания, команда и база специалистов.",
  independent_specialist: "Кабинет специалиста: структурированный профиль, ставки и доступность.",
  invited_member: "Кабинет участника компании: разделы определяются членством и permissions.",
  platform_staff: "Операционный кабинет Brainics: модерация, справочники и контроль данных.",
};

type StatusTone = "backend-ready" | "planned" | "in-progress";

interface NavItem {
  id: WorkspaceRouteName;
  label: string;
  roles?: UserRegistrationType[];
  status?: StatusTone;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const allCompanyRoles: UserRegistrationType[] = ["client_owner", "vendor_owner", "invited_member"];
const specialistOwnerRoles: UserRegistrationType[] = [
  "independent_specialist",
  "vendor_owner",
  "platform_staff",
];

const navSections: NavSection[] = [
  {
    title: "Основное",
    items: [
      { id: "dashboard", label: "Обзор", status: "backend-ready" },
      { id: "profile", label: "Аккаунт", status: "backend-ready" },
    ],
  },
  {
    title: "Компания",
    items: [
      { id: "company", label: "Профиль компании", roles: allCompanyRoles, status: "backend-ready" },
      { id: "companyMembers", label: "Участники", roles: allCompanyRoles, status: "backend-ready" },
      { id: "companyFiles", label: "Файлы", roles: allCompanyRoles, status: "backend-ready" },
      {
        id: "companyModeration",
        label: "Модерация компаний",
        roles: ["platform_staff"],
        status: "backend-ready",
      },
    ],
  },
  {
    title: "Специалисты",
    items: [
      {
        id: "specialists",
        label: "База специалистов",
        roles: ["vendor_owner", "platform_staff"],
        status: "backend-ready",
      },
      {
        id: "specialistProfile",
        label: "Профиль специалиста",
        roles: specialistOwnerRoles,
        status: "backend-ready",
      },
      {
        id: "specialistSkills",
        label: "Навыки",
        roles: specialistOwnerRoles,
        status: "backend-ready",
      },
      {
        id: "specialistExperience",
        label: "Опыт",
        roles: specialistOwnerRoles,
        status: "backend-ready",
      },
      {
        id: "specialistPortfolio",
        label: "Портфолио",
        roles: specialistOwnerRoles,
        status: "backend-ready",
      },
      {
        id: "specialistEducation",
        label: "Образование",
        roles: specialistOwnerRoles,
        status: "backend-ready",
      },
      {
        id: "specialistRates",
        label: "Ставки",
        roles: specialistOwnerRoles,
        status: "backend-ready",
      },
      {
        id: "specialistAvailability",
        label: "Доступность",
        roles: specialistOwnerRoles,
        status: "backend-ready",
      },
      { id: "resume", label: "Резюме", roles: specialistOwnerRoles, status: "planned" },
    ],
  },
  {
    title: "Работа",
    items: [
      {
        id: "jobs",
        label: "Вакансии",
        roles: ["client_owner", "platform_staff"],
        status: "planned",
      },
      {
        id: "matching",
        label: "Подбор",
        roles: ["client_owner", "vendor_owner", "platform_staff"],
        status: "planned",
      },
      {
        id: "assignments",
        label: "Назначения",
        roles: ["client_owner", "vendor_owner", "invited_member", "platform_staff"],
        status: "planned",
      },
    ],
  },
  {
    title: "Администрирование",
    items: [
      {
        id: "dictionaries",
        label: "Справочники",
        roles: ["platform_staff"],
        status: "backend-ready",
      },
    ],
  },
];

interface PageMeta {
  eyebrow: string;
  title: string;
  description: string;
  status: StatusTone;
  cards: Array<{ title: string; text: string }>;
}

const pageMeta: Record<WorkspaceRouteName, PageMeta> = {
  dashboard: {
    eyebrow: "Рабочее пространство",
    title: "Обзор",
    description: "Краткое состояние аккаунта и доступных разделов.",
    status: "backend-ready",
    cards: [],
  },
  profile: {
    eyebrow: "Учётная запись",
    title: "Аккаунт",
    description: "Базовые данные текущей подтверждённой учётной записи.",
    status: "backend-ready",
    cards: [],
  },
  company: {
    eyebrow: "Компания",
    title: "Профиль компании",
    description:
      "Карточка компании, юридические данные, контакты, статус заполнения и отправка на модерацию.",
    status: "backend-ready",
    cards: [
      {
        title: "Профиль",
        text: "Название, тип компании, страна, город, сайт, отрасль и описание.",
      },
      {
        title: "Статус",
        text: "Состояние onboarding и модерации: draft, pending review, verified или rejected.",
      },
      {
        title: "Действия",
        text: "Редактирование, сохранение черновика и отправка полной компании на проверку.",
      },
    ],
  },
  companyMembers: {
    eyebrow: "Компания",
    title: "Участники",
    description: "Команда компании, приглашения и управление доступом менеджеров.",
    status: "backend-ready",
    cards: [
      { title: "Список участников", text: "Владельцы и менеджеры с текущим статусом членства." },
      { title: "Приглашения", text: "Создание и повторная отправка приглашений на email." },
      {
        title: "Доступ",
        text: "Активация и деактивация менеджеров без передачи владения компанией.",
      },
    ],
  },
  companyFiles: {
    eyebrow: "Компания",
    title: "Файлы",
    description:
      "Метаданные логотипов и документов компании, а также будущая точка подключения объектного хранилища.",
    status: "backend-ready",
    cards: [
      { title: "Логотипы", text: "Изображения компании с проверкой типа файла и размера." },
      { title: "Документы", text: "Company-visible файлы с server-side проверкой доступа." },
      { title: "Внутренние заметки", text: "Brainics-only заметки для операционной команды." },
    ],
  },
  companyModeration: {
    eyebrow: "Brainics staff",
    title: "Модерация компаний",
    description: "Очередь компаний на проверку и операционные решения по статусам.",
    status: "backend-ready",
    cards: [
      { title: "Очередь", text: "Фильтры по типу компании, статусу и дате отправки." },
      {
        title: "Решения",
        text: "Verify, reject, block и archive с обязательными причинами там, где они нужны.",
      },
      { title: "Аудит", text: "История действий по отправке и модерации компании." },
    ],
  },
  specialists: {
    eyebrow: "Специалисты",
    title: "База специалистов",
    description:
      "Список независимых и vendor-owned специалистов, доступных по текущему контексту компании.",
    status: "backend-ready",
    cards: [
      { title: "Профили", text: "Основная информация, специализация, грейд и статус профиля." },
      { title: "Фильтры", text: "Будущая основа для поиска по навыкам, ставкам и доступности." },
      {
        title: "Контекст",
        text: "Vendor-owned профили остаются внутри границ компании-подрядчика.",
      },
    ],
  },
  specialistProfile: {
    eyebrow: "Специалист",
    title: "Профиль специалиста",
    description: "Личные и профессиональные секции структурированного профиля.",
    status: "backend-ready",
    cards: [
      { title: "Основное", text: "Имя, локация, часовой пояс, summary и годы опыта." },
      { title: "Профессия", text: "Специализация и грейд из управляемых справочников." },
      { title: "Статус", text: "Draft, active или archived с безопасными server-side границами." },
    ],
  },
  specialistSkills: {
    eyebrow: "Специалист",
    title: "Навыки",
    description: "Навыки специалиста, уровни, опыт и признаки верификации.",
    status: "backend-ready",
    cards: [
      { title: "Справочник", text: "Навык выбирается из canonical skill taxonomy." },
      { title: "Уровень", text: "Оценка от 1 до 5, годы опыта и дата последнего использования." },
      { title: "Верификация", text: "Отдельная platform boundary для подтверждения навыков." },
    ],
  },
  specialistExperience: {
    eyebrow: "Специалист",
    title: "Опыт работы",
    description: "История работы специалиста с ролями, датами и достижениями.",
    status: "backend-ready",
    cards: [
      { title: "Позиции", text: "Компания, роль, даты, current-position flag и отрасль." },
      { title: "Описание", text: "Responsibilities, achievements и стек технологий." },
      { title: "Порядок", text: "Sort order для отображения в профиле." },
    ],
  },
  specialistPortfolio: {
    eyebrow: "Специалист",
    title: "Портфолио",
    description: "Проектный опыт, результаты, стек и ссылки.",
    status: "backend-ready",
    cards: [
      { title: "Проекты", text: "Название, роль, период и признак текущего проекта." },
      { title: "Результаты", text: "Задачи, результат, индустрия и стек." },
      { title: "NDA", text: "Отдельный флаг для приватных проектов." },
    ],
  },
  specialistEducation: {
    eyebrow: "Специалист",
    title: "Образование и языки",
    description: "Образование, сертификаты и языки специалиста.",
    status: "backend-ready",
    cards: [
      { title: "Образование", text: "Учебные заведения, степени, специальности и годы." },
      { title: "Сертификаты", text: "Название, провайдер, дата выдачи и credential URL." },
      { title: "Языки", text: "Уровни владения от A1 до native." },
    ],
  },
  specialistRates: {
    eyebrow: "Специалист",
    title: "Ставки и формат работы",
    description: "Rate model, валюта, диапазоны ставок, занятость и предпочтения.",
    status: "backend-ready",
    cards: [
      { title: "Ставки", text: "Hourly/monthly ranges, negotiable и валюта." },
      { title: "Формат", text: "Remote, hybrid, onsite или flexible." },
      { title: "Загрузка", text: "Employment type и desired workload." },
    ],
  },
  specialistAvailability: {
    eyebrow: "Специалист",
    title: "Доступность",
    description: "Текущая доступность, дата выхода, недельная ёмкость и загрузка.",
    status: "backend-ready",
    cards: [
      { title: "Availability", text: "Available, available from, unavailable или not looking." },
      { title: "Capacity", text: "Часы в неделю и текущая загрузка в процентах." },
      { title: "Confirmation", text: "Дата последнего подтверждения и заметки." },
    ],
  },
  resume: {
    eyebrow: "Специалист",
    title: "Резюме",
    description: "Загрузка PDF, DOC, DOCX и TXT будет подключена после backend BRN-080.",
    status: "planned",
    cards: [
      { title: "Файл", text: "Приватное хранение и metadata резюме." },
      { title: "Парсинг", text: "Будущая точка для извлечения данных профиля." },
      { title: "Доступ", text: "Файл не должен быть публичным без разрешения." },
    ],
  },
  jobs: {
    eyebrow: "Работа",
    title: "Вакансии",
    description: "Раздел вакансий относится к Phase 03 и пока показывается как будущий модуль.",
    status: "planned",
    cards: [
      { title: "Заявки", text: "Создание потребности клиента и требований к специалисту." },
      { title: "Статусы", text: "Черновик, опубликовано, в подборе, закрыто." },
      { title: "Связь с matching", text: "Вакансия станет входной точкой для подбора." },
    ],
  },
  matching: {
    eyebrow: "Работа",
    title: "Подбор",
    description: "Будущий объяснимый matching между вакансиями и специалистами.",
    status: "planned",
    cards: [
      { title: "Кандидаты", text: "Ранжированный список подходящих специалистов." },
      { title: "Объяснения", text: "Почему специалист подходит или не подходит." },
      { title: "Фильтры", text: "Навыки, ставка, доступность, формат работы и опыт." },
    ],
  },
  assignments: {
    eyebrow: "Работа",
    title: "Назначения",
    description: "Будущий workflow после выбора специалиста на проект.",
    status: "planned",
    cards: [
      { title: "Назначение", text: "Кто, куда и на какой период назначен." },
      { title: "Ставка", text: "Фиксация коммерческих условий." },
      { title: "Табели", text: "Связь с будущей Phase 04." },
    ],
  },
  dictionaries: {
    eyebrow: "Brainics staff",
    title: "Справочники",
    description: "Операционное управление специализациями, грейдами и навыками.",
    status: "backend-ready",
    cards: [
      { title: "Специализации", text: "Управляемый справочник направлений." },
      { title: "Грейды", text: "Уровни специалистов с сортировкой." },
      { title: "Навыки", text: "Категории, навыки и синонимы." },
    ],
  },
};

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function statusLabel(status: StatusTone): string {
  switch (status) {
    case "backend-ready":
      return "Backend готов";
    case "in-progress":
      return "В работе";
    case "planned":
      return "Скоро";
  }
}

function statusClassName(status: StatusTone): string {
  switch (status) {
    case "backend-ready":
      return "bg-emerald-300/10 text-emerald-200";
    case "in-progress":
      return "bg-cyan-300/10 text-cyan-200";
    case "planned":
      return "bg-slate-700/50 text-slate-300";
  }
}

function visibleSections(user: AuthenticatedUser): NavSection[] {
  return navSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.roles || item.roles.includes(user.registrationType),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

interface WorkspaceLayoutProps {
  active: WorkspaceRouteName;
  children: ReactNode;
}

function WorkspaceLayout({ active, children }: WorkspaceLayoutProps) {
  const auth = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  if (!auth.user) return null;

  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError(null);

    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch (error) {
      setSignOutError(authErrorMessage(error));
      setSigningOut(false);
    }
  }

  const sections = visibleSections(auth.user);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 lg:grid lg:grid-cols-[280px_1fr]">
      <aside className="border-b border-white/10 bg-slate-950 lg:min-h-screen lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-5 py-5 lg:block lg:px-6">
          <button
            className="text-xl font-semibold tracking-tight"
            onClick={() => navigate("/")}
            type="button"
          >
            Brainics
          </button>
          <span className="rounded-full bg-cyan-300/10 px-3 py-1 text-xs font-medium text-cyan-200 lg:mt-3 lg:inline-block">
            Workspace
          </span>
        </div>

        <nav className="flex gap-2 overflow-x-auto px-4 pb-4 lg:block lg:space-y-5 lg:px-4 lg:pb-8">
          {sections.map((section) => (
            <div className="min-w-max lg:min-w-0" key={section.title}>
              <p className="mb-2 px-4 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                {section.title}
              </p>
              <div className="flex gap-2 lg:block lg:space-y-1">
                {section.items.map((item) => (
                  <button
                    className={`whitespace-nowrap rounded-xl px-4 py-3 text-left text-sm transition lg:w-full ${
                      active === item.id
                        ? "bg-cyan-300 text-slate-950"
                        : "text-slate-400 hover:bg-white/5 hover:text-white"
                    }`}
                    key={item.id}
                    onClick={() => navigate(workspaceRoutePaths[item.id])}
                    type="button"
                  >
                    <span>{item.label}</span>
                    {item.status === "planned" ? (
                      <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-300">
                        soon
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="min-w-0">
        <header className="border-b border-white/10 bg-slate-950/85 backdrop-blur">
          <div className="flex min-h-20 items-center justify-between gap-4 px-5 sm:px-8">
            <div className="min-w-0">
              <p className="truncate font-medium">
                {auth.user.firstName} {auth.user.lastName}
              </p>
              <p className="truncate text-xs text-slate-500">
                {registrationTypeLabels[auth.user.registrationType]}
              </p>
            </div>
            <button
              className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-300 transition hover:border-white/20 hover:bg-white/5 hover:text-white disabled:opacity-60"
              disabled={signingOut}
              onClick={() => void handleSignOut()}
              type="button"
            >
              {signingOut ? "Выходим…" : "Выйти"}
            </button>
          </div>
          {signOutError ? (
            <div className="border-t border-rose-300/10 bg-rose-300/10 px-5 py-2 text-sm text-rose-100 sm:px-8">
              {signOutError}
            </div>
          ) : null}
        </header>

        <main className="px-5 py-8 sm:px-8 lg:px-10 lg:py-10">{children}</main>
      </div>
    </div>
  );
}

function DashboardContent({ user, expiresAt }: { user: AuthenticatedUser; expiresAt: string }) {
  const sections = visibleSections(user);
  const visibleItems = sections.flatMap((section) => section.items);
  const backendReadyCount = visibleItems.filter((item) => item.status === "backend-ready").length;
  const plannedCount = visibleItems.filter((item) => item.status === "planned").length;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-cyan-300">Рабочее пространство</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
            Добро пожаловать, {user.firstName}
          </h1>
          <p className="mt-3 max-w-2xl leading-7 text-slate-400">
            {roleHints[user.registrationType]}
          </p>
        </div>
        <span className="w-fit rounded-full bg-emerald-300/10 px-3 py-1.5 text-xs font-medium text-emerald-200">
          Сессия активна
        </span>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-4">
        <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-6">
          <p className="text-sm text-slate-500">Тип регистрации</p>
          <p className="mt-3 text-xl font-semibold">
            {registrationTypeLabels[user.registrationType]}
          </p>
        </article>
        <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-6">
          <p className="text-sm text-slate-500">Доступные разделы</p>
          <p className="mt-3 text-xl font-semibold text-cyan-200">{visibleItems.length}</p>
        </article>
        <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-6">
          <p className="text-sm text-slate-500">Backend готов</p>
          <p className="mt-3 text-xl font-semibold text-emerald-200">{backendReadyCount}</p>
        </article>
        <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-6">
          <p className="text-sm text-slate-500">Сессия действует до</p>
          <p className="mt-3 text-lg font-semibold">{formatDateTime(expiresAt)}</p>
        </article>
      </div>

      <section className="mt-8 rounded-3xl border border-cyan-300/15 bg-cyan-300/[0.06] p-6 sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-200">
          Frontend roadmap
        </p>
        <h2 className="mt-3 text-2xl font-semibold">Кабинеты подключаются по ролям</h2>
        <p className="mt-3 max-w-3xl leading-7 text-slate-300">
          Навигация уже показывает будущую структуру кабинетов. Разделы с готовым backend можно
          подключать к API формам следующими PR, а будущие модули остаются помечены как “Скоро”.
        </p>
      </section>

      <section className="mt-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold">Разделы кабинета</h2>
          <span className="text-xs text-slate-600">Скоро: {plannedCount}</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visibleItems.map((item) => (
            <button
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-left transition hover:border-cyan-300/30 hover:bg-cyan-300/[0.04]"
              key={item.id}
              onClick={() => navigate(workspaceRoutePaths[item.id])}
              type="button"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium text-slate-200">{item.label}</p>
                <span
                  className={`rounded-full px-2.5 py-1 text-[11px] ${statusClassName(item.status ?? "backend-ready")}`}
                >
                  {statusLabel(item.status ?? "backend-ready")}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-500">{pageMeta[item.id].description}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function AccountProfileContent({
  user,
  expiresAt,
}: {
  user: AuthenticatedUser;
  expiresAt: string;
}) {
  const rows = [
    ["Имя", `${user.firstName} ${user.lastName}`],
    ["Email", user.email],
    ["Тип регистрации", registrationTypeLabels[user.registrationType]],
    ["Статус", "Активен, email подтверждён"],
    ["Сессия действует до", formatDateTime(expiresAt)],
  ];

  return (
    <div className="mx-auto max-w-4xl">
      <p className="text-sm font-semibold text-cyan-300">Учётная запись</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">Аккаунт</h1>
      <p className="mt-3 text-slate-400">Базовые данные текущей подтверждённой учётной записи.</p>

      <section className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035]">
        {rows.map(([label, value], index) => (
          <div
            className={`grid gap-2 px-6 py-5 sm:grid-cols-[220px_1fr] ${
              index === rows.length - 1 ? "" : "border-b border-white/10"
            }`}
            key={label}
          >
            <p className="text-sm text-slate-500">{label}</p>
            <p className="break-words font-medium text-slate-200">{value}</p>
          </div>
        ))}
      </section>

      <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm leading-6 text-slate-500">
        Тип регистрации используется для UX и онбординга. Реальный доступ к данным определяется
        backend permissions, company membership и server-side context.
      </div>
    </div>
  );
}

function ModuleContent({ routeName }: { routeName: WorkspaceRouteName }) {
  const meta = pageMeta[routeName];

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-cyan-300">{meta.eyebrow}</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{meta.title}</h1>
          <p className="mt-3 max-w-3xl leading-7 text-slate-400">{meta.description}</p>
        </div>
        <span
          className={`w-fit rounded-full px-3 py-1.5 text-xs font-medium ${statusClassName(meta.status)}`}
        >
          {statusLabel(meta.status)}
        </span>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {meta.cards.map((card) => (
          <article
            className="rounded-2xl border border-white/10 bg-white/[0.035] p-6"
            key={card.title}
          >
            <p className="text-lg font-semibold text-slate-100">{card.title}</p>
            <p className="mt-3 text-sm leading-6 text-slate-500">{card.text}</p>
          </article>
        ))}
      </div>

      <section className="mt-8 rounded-3xl border border-white/10 bg-white/[0.025] p-6 sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
          Следующий шаг
        </p>
        {meta.status === "backend-ready" ? (
          <>
            <h2 className="mt-3 text-2xl font-semibold">Подключить реальные API-формы</h2>
            <p className="mt-3 max-w-3xl leading-7 text-slate-400">
              Backend для этого раздела уже реализован. Следующий frontend PR может заменить эту
              страницу-заглушку на список, форму редактирования, empty/error/loading states и
              действия с серверной проверкой permissions.
            </p>
          </>
        ) : (
          <>
            <h2 className="mt-3 text-2xl font-semibold">
              Раздел зависит от следующих backend-задач
            </h2>
            <p className="mt-3 max-w-3xl leading-7 text-slate-400">
              Сейчас страница добавлена в карту кабинета, чтобы пользователь видел будущую структуру
              продукта. Полная функциональность появится после соответствующих backend задач из
              roadmap.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

export function WorkspacePage({ routeName }: { routeName: WorkspaceRouteName }) {
  const auth = useAuth();
  if (!auth.user || !auth.expiresAt) return null;

  let content: ReactNode;
  if (routeName === "dashboard") {
    content = <DashboardContent expiresAt={auth.expiresAt} user={auth.user} />;
  } else if (routeName === "profile") {
    content = <AccountProfileContent expiresAt={auth.expiresAt} user={auth.user} />;
  } else if (routeName === "company") {
    content = <CompanyProfilePage />;
  } else if (routeName === "companyMembers") {
    content = <CompanyMembersPage />;
  } else if (routeName === "companyFiles") {
    content = <CompanyFilesPage />;
  } else if (routeName === "companyModeration") {
    content = <CompanyModerationPage />;
  } else if (routeName === "specialistProfile") {
    content = <SpecialistProfilePage />;
  } else if (routeName === "specialistSkills") {
    content = <SpecialistSkillsPage />;
  } else if (routeName === "specialistExperience") {
    content = <SpecialistExperiencePage />;
  } else if (routeName === "specialistPortfolio") {
    content = <SpecialistPortfolioPage />;
  } else if (routeName === "specialistEducation") {
    content = <SpecialistEducationPage />;
  } else if (routeName === "specialistRates") {
    content = <SpecialistRatesPage />;
  } else if (routeName === "specialistAvailability") {
    content = <SpecialistAvailabilityPage />;
  } else {
    content = <ModuleContent routeName={routeName} />;
  }

  return <WorkspaceLayout active={routeName}>{content}</WorkspaceLayout>;
}

export function DashboardPage() {
  return <WorkspacePage routeName="dashboard" />;
}

export function ProfilePage() {
  return <WorkspacePage routeName="profile" />;
}

export function SessionLoadingPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-6 text-slate-100">
      <div className="text-center">
        <span className="mx-auto block h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-cyan-300" />
        <p className="mt-5 text-sm text-slate-400">Проверяем защищённую сессию…</p>
      </div>
    </div>
  );
}
